import { memo, Fragment } from 'react';

export interface PlansSummaryCardProps {
    averageBronze: string;
    averageSilver: string;
    averageGold: string;
    averagePlatinum: string;
    averageCatastrophic: string;
    lowestBronze: string;
    lowestSilver: string;
    lowestGold: string;
    lowestPlatinum: string;
    lowestCatastrophic: string;
}

export const PlansSummaryCard = memo(({
    averageBronze,
    averageSilver,
    averageGold,
    averagePlatinum,
    averageCatastrophic,
    lowestBronze,
    lowestSilver,
    lowestGold,
    lowestPlatinum,
    lowestCatastrophic,
}: PlansSummaryCardProps) => {
    const getMetalTagStyles = (name: string): string => {
        const base = 'inline-flex items-center justify-center rounded-lg px-2.5 py-1 text-sm md:text-base font-medium';
        switch (name) {
            case 'Catastrophic':
                return `${base} bg-[#CC6076] text-white`;
            case 'Bronze':
                return `${base} bg-[#E59A42] text-white`;
            case 'Silver':
                return `${base} bg-[#A8AFB7] text-white`;
            case 'Gold':
                return `${base} bg-[#FAE97A] text-[#94832C]`;
            case 'Platinum':
                return `${base} bg-[#8FADCF] text-white`;
            default:
                return `${base} bg-gray-100 text-gray-600`;
        }
    };

    const metalLevels = [
        { name: 'Catastrophic', average: averageCatastrophic, lowest: lowestCatastrophic },
        { name: 'Bronze', average: averageBronze, lowest: lowestBronze },
        { name: 'Silver', average: averageSilver, lowest: lowestSilver },
        { name: 'Gold', average: averageGold, lowest: lowestGold },
        { name: 'Platinum', average: averagePlatinum, lowest: lowestPlatinum },
    ];

    return (
        <div className="w-full rounded-xl border-l-3 border-gray-600 p-4 md:p-6 shadow-[1px_1px_5.2px_0px_#00000026]">
            {/* Desktop (lg+): 3 rows × 6 columns */}
            <div className="hidden lg:grid lg:grid-cols-6 lg:gap-4">
                {/* Row 1: Headers */}
                <div className="flex flex-col gap-1">
                    <label className="text-sm md:text-lg font-semibold text-gray-900">Metal Level</label>
                </div>
                {metalLevels.map((level) => (
                    <div key={level.name} className="flex flex-col gap-1 items-center">
                        <span className={`${getMetalTagStyles(level.name)} w-fit`}>{level.name}</span>
                    </div>
                ))}

                {/* Row 2: Average */}
                <div className="flex flex-col gap-1">
                    <label className="text-sm md:text-lg font-semibold text-gray-900">Average</label>
                </div>
                {metalLevels.map((level) => (
                    <div key={`${level.name}-avg`} className="flex flex-col gap-1 items-center">
                        <span className="text-sm md:text-base font-medium text-gray-900">{level.average}</span>
                    </div>
                ))}

                {/* Row 3: Lowest */}
                <div className="flex flex-col gap-1">
                    <label className="text-sm md:text-lg font-semibold text-gray-900">Lowest</label>
                </div>
                {metalLevels.map((level) => (
                    <div key={`${level.name}-low`} className="flex flex-col gap-1 items-center">
                        <span className="text-sm md:text-base font-medium text-gray-900">{level.lowest}</span>
                    </div>
                ))}
            </div>

            {/* Mobile and Tablet: 3 columns × 6 rows */}
            <div className="grid grid-cols-3 gap-3 lg:hidden">
                {/* Row 1: Headers */}
                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Metal Level</label>
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Average</label>
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Lowest</label>
                </div>

                {/* Rows 2-6: Metal levels */}
                {metalLevels.map((level) => (
                    <Fragment key={level.name}>
                        <div className="flex flex-col gap-1">
                            <span className={`${getMetalTagStyles(level.name)} w-fit`}>{level.name}</span>
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="w-full text-sm font-medium text-gray-900">{level.average}</span>
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="w-full text-sm font-medium text-gray-900">{level.lowest}</span>
                        </div>
                    </Fragment>
                ))}
            </div>
        </div>
    );
});
